import { useEffect, useState } from "react";
import { ReportButton } from "./ReportButton";
import { Backdrop, Button, CircularProgress, Divider, FormControl, IconButton, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import WeekPicker from "./WeekPicker";
import { ReportsTable } from "./ReportsTable";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ServiceApi } from "../../services/index.service";
import dayjs from "dayjs";
import updateLocale from 'dayjs/plugin/updateLocale'
import moment from "moment";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import { ProductionTypeCategoryList } from "../../utils";

// Setting Start Day of a week to Monday
dayjs.extend(updateLocale)
dayjs.updateLocale('en', { weekStart: 1 })
moment.updateLocale("en", { week: { dow: 1 } });

const reportTypes = [
    { label: 'Flavor Made Report', value: 'made' },
    { label: 'Flavor Sold Report', value: 'sold' },
    { label: 'Flavor Thrown Report', value: 'throw' }
];

const ProductionReports = ({ type = "" }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { storage } = useSelector((state) => state);
    const serviceapi = new ServiceApi();
    const [reportType, setReportType] = useState('made');
    const [groupBy, setGroupBy] = useState('WEEKLY');
    const [openMonthPicker, setOpenMonthPicker] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [reportsData, setReportsData] = useState([]);
    const [dates, setDates] = useState({
        start: dayjs(moment().startOf('week').toDate()),
        end: dayjs(moment().endOf('week').toDate())
    });
    const [csvData, setCSVData] = useState({ headers: [], data: [] });

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
    };

    useEffect(() => {
        if (reportsData?.data?.length > 0) {
            handleExportCSV(reportType);
        }
    }, [reportsData, reportType]);

    useEffect(() => {
        if (storage?.store?.intStoreID && dates.start && dates.end)
            getReportsData();
    }, [storage?.store?.intStoreID, type, dates.start, dates.end]);

    useEffect(() => {
        if (groupBy === 'WEEKLY') {
            setDates({
                start: dayjs(moment().startOf('week').toDate()),
                end: dayjs(moment().endOf('week').toDate())
            });
        }
        else {
            setDates({
                start: dayjs(moment().startOf('month').toDate()),
                end: dayjs(moment().endOf('month').toDate())
            });
        }
    }, [groupBy]);

    const getReportsData = async () => {
        const params = {
            intStoreId: storage?.store?.intStoreID,
            startDate: dayjs(dates.start).format('YYYY-MM-DD'),
            endDate: dayjs(dates.end).format('YYYY-MM-DD'),
            groupBy: groupBy,
            type: type || 'flavor'
        };
        setLoading(true);
        try {
            const reports = await serviceapi.getProductionReports(params);
            setReportsData(reports);
        }
        catch (err) {
            setReportsData([]);
            enqueueSnackbar(err?.response?.data?.message || 'Something went wrong, please try again!', {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error"
            });
        }
        finally {
            setLoading(false);
        }
    };

    const handleMonthChange = (date) => {
        setOpenMonthPicker(false);
        const startOfMonth = dayjs(moment(new Date(date)).startOf('month').toDate());
        const endOfMonth = dayjs(moment(new Date(date)).endOf('month').toDate());
        setDates({ start: startOfMonth, end: endOfMonth });
    };

    const getSearchResults = (productionData, searchKey) => {
        let production = JSON.parse(JSON.stringify(productionData));
        production = production.map(obj => ({ ...obj, flavorsList: obj.flavorsList?.filter(f => (f.flavor_name)?.toLowerCase().includes(searchKey.toLowerCase())) }));
        return production;
    };

    const reports = searchText.length > 0 ? getSearchResults(reportsData?.data || [], searchText) : reportsData?.data || [];

    const ResultInformationText = () => {
        const dateRange = reportsData?.groupBy === 'WEEKLY'
            ? (dayjs(reportsData?.startDate).format('D MMM') + " - " + dayjs(reportsData?.endDate).format('D MMM, YYYY'))
            : dayjs(reportsData?.startDate).format('MMMM YYYY');
        return <p className="text-xs mb-2">
            Showing Flavor
            <span className="capitalize"> {reportType === 'throw' ? 'Thrown' : reportType} </span>
            Result of
            <span className="font-semibold ml-1">{dateRange}</span>
        </p>
    };

    const handleExportCSV = (reportType) => {
        const defaultHeaders = [{ label: "Category", key: 'category' }, { label: "Flavor", key: 'flavor' }];
        const csvHeaders = [
            ...defaultHeaders,
            ...reportsData?.headers?.map(({ label, date, id }) => ({ label: type !== 'weather' ? (reportsData?.groupBy === "WEEKLY" ? dayjs(date).format('D MMM') : label) : `${label}° F`, key: `${id}` })),
            { label: "Total", key: 'flvTotal' }
        ];
        let totalsRow = { category: "", flavor: "Total" };
        let grandTotal = 0;
        const data = reportsData?.data?.reduce((acc, cur) => {
            const { product_type_name: productTypeName } = ProductionTypeCategoryList?.find((j) => j?.intProductTypeID === cur.intProductTypeID);
            const filteredFlavorsList = cur?.flavorsList?.reduce((result, flv) => {
                const hasData = Object.values(flv.reports).some(report => report[reportType]);
                if (hasData) return [...result, flv];
                else return result;
            }, []);
            const flavors = filteredFlavorsList?.map(({ flavor_name, reports }, i) => {
                let flv = {
                    category: productTypeName,
                    flavor: flavor_name
                };
                let flvTotal = 0;
                for (const key in reports) {
                    flv[key] = reports[key][reportType];
                    flvTotal += reports[key][reportType];
                    totalsRow[key] = (totalsRow[key] || 0) + reports[key][reportType];
                }
                flv.flvTotal = flvTotal;
                grandTotal += flv.flvTotal;
                return flv;
            });
            return [...acc, ...flavors];
        }, []);
        totalsRow.flvTotal = grandTotal;
        setCSVData({ headers: csvHeaders, data: [...data, { ...totalsRow }] });
    };

    return (
        <>
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <div className="mt-12 p-1">
                <div className="m-2 mt-6 p-4 bg-white">
                    <h1 className="mb-2 text-lg font-semibold">Reports</h1>
                    {reportTypes.map(({ label, value }, i) => (
                        <ReportButton key={i} label={label} type={value} isSelected={reportType === value} onClick={setReportType} />
                    ))}
                    <br></br>
                    <ToggleButtonGroup
                        className="mt-5 border-slate-400"
                        value={groupBy}
                        exclusive
                        onChange={(...arr) => arr[1] !== null && setGroupBy(arr[1])}
                        aria-label="Group-By"
                        size="small"
                    >
                        <ToggleButton classes={{ selected: "bg-white text-primary border-primary" }} className="capitalize px-3 text-xs py-2" value="WEEKLY">Weekly</ToggleButton>
                        <ToggleButton classes={{ selected: "bg-white text-primary border-primary" }} className="capitalize px-3 text-xs py-2" value="MONTHLY">Monthly</ToggleButton>
                    </ToggleButtonGroup>
                    {groupBy === "WEEKLY" ?
                        <WeekPicker
                            value={dates.start}
                            setValue={(date) => {
                                const startOfWeek = dayjs(moment(new Date(date)).startOf('week').toDate());
                                const endOfWeek = dayjs(moment(new Date(date)).endOf('week').toDate());
                                setDates({ start: startOfWeek, end: endOfWeek });
                            }} />
                        : <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker disableFuture value={dates.start} onMonthChange={handleMonthChange} slotProps={{ textField: { inputProps: { className: 'text-xs w-24 pr-0' } }, }} className="mt-5 ml-3 text-xs" views={['year', 'month']} view="month" />
                        </LocalizationProvider>}
                    <hr className="my-3" />
                    {reportsData?.startDate && <ResultInformationText />}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center mr-3 mb-2">
                            <FormControl className="w-60" size="small">
                                <TextField
                                    placeholder="Search by Flavor name"
                                    value={searchText}
                                    onChange={handleSearchChange}
                                    InputProps={{ endAdornment: <>{searchText && <IconButton title="Clear" onClick={() => setSearchText('')} size="small"><XMarkIcon className="h-5 w-5 text-gray-400" /></IconButton>}<MagnifyingGlassIcon className="h-6 w-6 text-gray-400" /></> }}
                                    inputProps={{ className: "text-sm" }}
                                />
                            </FormControl>
                            {type === 'weather' && <>
                                <Divider variant="middle" orientation="vertical" className="mx-4 my-0 h-9 border-x" />
                                <p className="font-medium text-sm text-gray-500">Note: Flavors made of different weather conditions</p>
                            </>}
                        </div>
                        {reports.length > 0 && <CSVLink filename="Reports" headers={csvData?.headers} data={csvData?.data} >
                            <Button className="text-[#002EA5] capitalize text-xs" startIcon={<img src="../../assets/icons/file-csv.png" width={18} />}>Export CSV</Button>
                        </CSVLink>}
                    </div>
                    {reports.length > 0 ?
                        <ReportsTable type={type} groupBy={reportsData?.groupBy} headers={reportsData?.headers} data={reports} reportType={reportType} />
                        : <p className="my-5 text-center">No Reports found on selected date.</p>
                    }
                </div>
            </div>
        </>
    )
}

export default ProductionReports;